import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import polarrideLogo from '@/assets/polarride-logo-white.webp';
import { usePermissions } from '@/hooks/usePermissions';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Divider,
  Collapse,
  Badge,
  useTheme,
} from '@mui/material';
import {
  Dashboard,
  DirectionsCar,
  People,
  Person,
  VerifiedUser,
  Gavel,
  Analytics,
  Settings,
  ExpandLess,
  ExpandMore,
  BookOnline,
  CarRental,
  SupportAgent,
  ManageAccounts,
  ListAlt,
  DriveEta,
  AssignmentInd,
  Assessment,
  Security,
  ReportProblem,
  TrendingUp,
  Payments,
  Business,
  Science,
  CheckCircle,
  Psychology,
  MonitorHeart,
  Public,
  PsychologyAlt,
  History,
  ContactPhone,
  AddCircle,
  RateReview,
  CarRepair,
  NotificationsActive,
  Share,
  VpnKey,
  Monitor,
  Campaign,
  AccountCircle,
  HomeWork,
  SailingOutlined,
  BusinessCenter,
  Star,
  CardGiftcard,
  LocalOffer,
  AccountBalance,
  Badge as BadgeIcon,
  Diamond,
  Email,
  ListAltOutlined,
  AdminPanelSettings,
  ViewCarousel,
  Flight,
  AttachMoney,
  Handshake,
  FilterList,
  NotificationImportant,
} from '@mui/icons-material';

interface SidebarProps {
  open: boolean;
  onToggle: () => void;
  width: number;
  isMobile: boolean;
}

interface NavigationItem {
  id: string;
  title: string;
  icon: React.ReactNode;
  path?: string;
  badge?: number;
  children?: NavigationItem[];
  requiredPermission?: { resource: string; action: string };
  allowedRoles?: string[];
}

const navigationItems: NavigationItem[] = [
  {
    id: 'dashboard',
    title: 'Dashboard',
    icon: <Dashboard />,
    path: '/dashboard',
  },
  
  // 📋 CORE OPERATIONS - Daily workflow items
  {
    id: 'operations',
    title: 'Core Operations',
    icon: <AssignmentInd />,
    children: [
      { id: 'bookings-overview', title: 'Bookings Overview', icon: <BookOnline />, path: '/bookings/all', badge: 12 },
      { id: 'users', title: 'User Hub', icon: <People />, path: '/users/hub' },
      { id: 'corporate-accounts', title: 'Corporate Accounts', icon: <BusinessCenter />, path: '/admin/corporate-accounts', requiredPermission: { resource: 'corporate', action: 'view' } },
      { id: 'partnerships', title: 'Partnerships', icon: <Handshake />, path: '/admin/partnerships' },
      { id: 'partnership-applications', title: 'Partnership Applications', icon: <RateReview />, path: '/admin/partnership-applications' },
      { id: 'partnership-settings', title: 'Partnership Settings', icon: <Settings />, path: '/admin/partnership-settings' },
      { id: 'verification', title: 'Verification Center', icon: <VerifiedUser />, path: '/verification/admin', badge: 8 },
      { id: 'display-names', title: 'Display Names', icon: <BadgeIcon />, path: '/display-names' },
      { id: 'approvals', title: 'Approvals & Registration', icon: <RateReview />, path: '/approvals', badge: 15 },
      { id: 'reviews', title: 'Reviews & Ratings', icon: <Star />, path: '/reviews' },
      { id: 'yacht-destinations', title: 'Yacht Destinations', icon: <Public />, path: '/operations/yacht-destinations' },
      { id: 'follow-up', title: 'Follow-up Dashboard', icon: <ContactPhone />, path: '/follow-up', badge: 47 },
      { id: 'disputes', title: 'Disputes & Resolutions', icon: <Gavel />, path: '/disputes', badge: 3 },
    ],
  },

  // 🚗 FLEET & BOOKING MANAGEMENT
  {
    id: 'fleet-bookings',
    title: 'Fleet & Bookings',
    icon: <DirectionsCar />,
    children: [
      { id: 'vehicles', title: 'Fleet Management Hub', icon: <DirectionsCar />, path: '/vehicles' },
      { id: 'fleet-owners', title: 'Fleet Owners / Hosts', icon: <Business />, path: '/fleet-owners' },
      { id: 'fleet-managers', title: 'Fleet Manager Monitoring', icon: <BusinessCenter />, path: '/fleet-managers' },
      { id: 'fleet-subscriptions', title: 'Fleet Subscriptions', icon: <Diamond />, path: '/fleet-subscriptions' },
      { id: 'host-vehicles', title: 'Host Vehicle Applications', icon: <HomeWork />, path: '/host-vehicles', badge: 5 },
      { id: 'car-rentals', title: 'Car Rentals', icon: <CarRental />, path: '/bookings/rentals' },
      { id: 'yacht-rentals', title: 'Yacht Rentals', icon: <SailingOutlined />, path: '/bookings/yacht-rentals' },
      { id: 'driver-hires', title: 'Driver Hires', icon: <DriveEta />, path: '/bookings/driver-hires' },
      { id: 'vehicle-substitution', title: 'Vehicle Substitution', icon: <CarRepair />, path: '/bookings/substitution' },
      { id: 'service-areas', title: 'Service Areas (Airports)', icon: <Flight />, path: '/service-areas/airports' },
      { id: 'airport-pricing', title: 'Airport Transfer Pricing', icon: <AttachMoney />, path: '/pricing/airport-transfers' },
      { id: 'yacht-pricing', title: 'Yacht Destination Pricing', icon: <SailingOutlined />, path: '/pricing/yacht-destinations' },
    ],
  },

  // 👥 USER & DRIVER MANAGEMENT
  {
    id: 'people-management',
    title: 'People Management',
    icon: <ManageAccounts />,
    children: [
      { id: 'drivers', title: 'Driver Management', icon: <DriveEta />, path: '/drivers/management' },
      { id: 'finances', title: 'Financial Management', icon: <AccountBalance />, path: '/finances' },
      { id: 'host-analytics', title: 'Host Analytics', icon: <TrendingUp />, path: '/users/analytics' },
      { id: 'user-trust-safety', title: 'Trust & Safety', icon: <Security />, path: '/users/trust-safety' },
    ],
  },

  // 📊 ANALYTICS & COMPLIANCE
  {
    id: 'analytics-compliance',
    title: 'Analytics & Compliance',
    icon: <Analytics />,
    children: [
      { id: 'compliance', title: 'Compliance Metrics', icon: <CheckCircle />, path: '/compliance', badge: 5 },
      { id: 'regional-compliance', title: 'Regional Compliance', icon: <Public />, path: '/regional-compliance', badge: 7 },
      { id: 'sla-monitoring', title: 'SLA Monitoring', icon: <MonitorHeart />, path: '/sla-monitoring', badge: 3 },
      { id: 'analytics', title: 'Ratings & Reports', icon: <Assessment />, path: '/analytics' },
      { id: 'demand-intelligence', title: 'Demand Intelligence', icon: <PsychologyAlt />, path: '/demand-intelligence', badge: 4 },
      { id: 'audit-trail', title: 'Audit Trail', icon: <History />, path: '/audit-trail', badge: 8 },
    ],
  },

  // 🔧 BUSINESS TOOLS
  {
    id: 'business-tools',
    title: 'Business Tools',
    icon: <Business />,
    children: [
      { id: 'experiments', title: 'Experimentation Tools', icon: <Science />, path: '/experiments' },
      { id: 'education', title: 'Host Education', icon: <Psychology />, path: '/education', badge: 12 },
      { id: 'marketing-messages', title: 'Marketing Messages', icon: <Campaign />, path: '/marketing/messages' },
      { id: 'broadcast-messaging', title: 'Broadcast Messaging', icon: <Email />, path: '/marketing/broadcasts' },
      { id: 'push-notifications', title: 'Push Notifications', icon: <NotificationsActive />, path: '/push-notifications' },
      { id: 'referrals', title: 'Referrals Tracking', icon: <CardGiftcard />, path: '/referrals' },
      { id: 'referral-programs', title: 'Referral Programs', icon: <Campaign />, path: '/referral-programs' },
      { id: 'invitations', title: 'Admin Invitations', icon: <Email />, path: '/invitations' },
      { id: 'waitlist', title: 'Waitlist Requests', icon: <ListAltOutlined />, path: '/waitlist' },
      { id: 'promo-codes', title: 'Promo Codes', icon: <LocalOffer />, path: '/promo-codes' },
      { id: 'driver-campaigns', title: 'Driver Campaigns', icon: <Campaign />, path: '/driver-campaigns' },
      { id: 'vehicle-campaigns', title: 'Vehicle Campaigns', icon: <DirectionsCar />, path: '/vehicle-campaigns' },
      { id: 'carousels', title: 'Carousel Management', icon: <ViewCarousel />, path: '/carousels' },
      { id: 'favorites', title: 'Favorites', icon: <Star />, path: '/favorites' },
      { id: 'partners', title: 'Partner Management', icon: <Business />, path: '/partners' },
    ],
  },

  // 🔒 SECURITY
  {
    id: 'security',
    title: 'Security',
    icon: <Security />,
    children: [
      { id: 'bank-verification', title: 'Bank Verification', icon: <AccountBalance />, path: '/security/bank-verification' },
      { id: 'security-monitoring', title: 'Security Monitoring', icon: <Monitor />, path: '/security-monitoring' },
      { id: 'trip-sharing', title: 'Trip Sharing', icon: <Share />, path: '/trip-sharing' },
      { id: 'access-codes', title: 'Access Codes', icon: <VpnKey />, path: '/access-codes' },
      { id: 'admin-staff', title: 'Staff Management', icon: <ManageAccounts />, path: '/admin/staff' },
      { id: 'admin-roles', title: 'Role Permissions', icon: <AdminPanelSettings />, path: '/admin/roles' },
      { id: 'admin-alerts', title: 'Admin Alerts', icon: <NotificationImportant />, path: '/admin-alerts', allowedRoles: ['super_admin', 'admin'] },
      { id: 'admin-filters', title: 'Filter Management', icon: <FilterList />, path: '/admin/filters' },
    ],
  },

  // 👤 ADMIN PROFILE
  {
    id: 'profile',
    title: 'Admin Profile',
    icon: <AccountCircle />,
    path: '/profile',
  },

  // ⚙️ SETTINGS
  {
    id: 'settings',
    title: 'Settings',
    icon: <Settings />,
    path: '/settings',
  },

  // 🧪 DEVELOPMENT TOOLS
  {
    id: 'test-data',
    title: 'Test Data Init',
    icon: <Science />,
    path: '/test-data-initialization',
  },
];

export const Sidebar: React.FC<SidebarProps> = ({ open, onToggle, width, isMobile }) => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { can, isSuperAdmin, role } = usePermissions();
  const roleKey = String(role || '').toLowerCase().replace(/\s+/g, '_');
  const [expandedItems, setExpandedItems] = React.useState<string[]>(['operations', 'fleet-bookings', 'people-management']);

  const handleItemClick = (item: NavigationItem) => {
    if (item.children) {
      setExpandedItems(prev =>
        prev.includes(item.id)
          ? prev.filter(id => id !== item.id)
          : [...prev, item.id]
      );
    } else if (item.path) {
      navigate(item.path);
      if (isMobile) {
        onToggle();
      }
    }
  };

  const isActive = (path?: string) => {
    if (!path) return false;
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const hasPermissionForItem = (item: NavigationItem): boolean => {
    // Super admins can see everything
    if (isSuperAdmin()) return true;

    if (item.allowedRoles && !item.allowedRoles.includes(roleKey)) return false;

    // If no permission required, show to everyone
    if (!item.requiredPermission) return true;

    // Check specific permission
    return can(item.requiredPermission.resource, item.requiredPermission.action);
  };

  const filterItemsByPermission = (items: NavigationItem[]): NavigationItem[] => {
    return items
      .filter(item => hasPermissionForItem(item))
      .map(item => {
        if (item.children) {
          return {
            ...item,
            children: filterItemsByPermission(item.children)
          };
        }
        return item;
      })
      .filter(item => !item.children || item.children.length > 0); // Remove empty parent items
  };

  const renderNavigationItem = (item: NavigationItem, level = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.includes(item.id);
    const active = isActive(item.path);

    return (
      <React.Fragment key={item.id}>
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => handleItemClick(item)}
            sx={{
              pl: 2 + level * 2,
              pr: 2,
              py: 1,
              mx: 1,
              my: 0.25,
              borderRadius: 2,
              backgroundColor: active ? 'primary.main' : 'transparent',
              color: active ? 'primary.contrastText' : 'text.primary',
              '&:hover': {
                backgroundColor: active ? 'primary.dark' : 'action.hover',
              },
            }}
          >
            <ListItemIcon
              sx={{
                color: active ? 'primary.contrastText' : 'text.secondary',
                minWidth: 40,
              }}
            >
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.title}
              primaryTypographyProps={{
                fontSize: level === 0 ? '0.875rem' : '0.8125rem',
                fontWeight: active ? 600 : level === 0 ? 500 : 400,
              }}
            />
            {item.badge && (
              <Badge
                badgeContent={item.badge}
                color="error"
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '0.6875rem',
                    height: 16,
                    minWidth: 16,
                  },
                }}
              />
            )}
            {hasChildren && (
              <Box sx={{ ml: 1 }}>
                {isExpanded ? <ExpandLess /> : <ExpandMore />}
              </Box>
            )}
          </ListItemButton>
        </ListItem>

        {hasChildren && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List disablePadding>
              {item.children!.map(child => renderNavigationItem(child, level + 1))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo/Brand */}
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid',
          borderBottomColor: 'divider',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Box
          component="img"
          src={polarrideLogo}
          alt="PolarRide"
          sx={{
            height: 32,
            width: 'auto',
          }}
        />
      </Box>

      {/* Navigation */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', py: 1 }}>
        <List disablePadding>
          {filterItemsByPermission(navigationItems).map(item => renderNavigationItem(item))}
        </List>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          p: 2,
          borderTop: '1px solid',
          borderTopColor: 'divider',
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            textAlign: 'center',
            display: 'block',
          }}
        >
          Version 1.0.0
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'persistent'}
      open={open}
      onClose={isMobile ? onToggle : undefined}
      sx={{
        width: open ? width : 0,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: width,
          boxSizing: 'border-box',
          backgroundColor: 'background.paper',
          borderRight: '1px solid',
          borderRightColor: 'divider',
        },
      }}
      ModalProps={{
        keepMounted: true, // Better open performance on mobile
      }}
    >
      {drawer}
    </Drawer>
  );
};